import {DotFillIcon, MoonIcon, ShieldIcon, SyncIcon} from '@primer/octicons-react'
import type {
  ReviewRequestsData,
  CompletedReview,
  PendingReviewRequest,
} from '../../../page-data/payloads/review-requests'
import styles from './Reviewers.module.css'
import {pullRequestsOverviewRoute} from '../../../routes/overview-route'
import {pullRequestsChangesWithRangeRoute} from '../../../routes/files-changed-routes'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense} from 'react'
import {UserSectionSkeleton} from '../UserSectionSkeleton'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Link} from '@github-ui/react-core/link'
import {ReviewersLoader} from './ReviewersLoader'
import {ReviewerIdentity} from './ReviewerIdentity'
import {reviewState, reviewStateTooltip, deferredTooltip, codeOwnerTooltip} from './ReviewersShared'

interface CondensedReviewersProps {
  reviewers: ReviewRequestsData
}

export function CondensedReviewers() {
  return (
    <ErrorBoundary
      boundaryName="CondensedReviewers"
      fallback={<span className={styles.emptyState}>Unable to load reviewers</span>}
    >
      <Suspense fallback={<UserSectionSkeleton placeholderRows={2} />}>
        <ReviewersLoader>{reviewers => <CondensedReviewersComponent reviewers={reviewers} />}</ReviewersLoader>
      </Suspense>
    </ErrorBoundary>
  )
}

/** Reviewers section in the sidebar */
export function CondensedReviewersComponent({reviewers}: CondensedReviewersProps) {
  const completedReviews = reviewers.completedReviews ?? []
  const pendingRequests = reviewers.pendingReviewRequests ?? []
  const suggestedReviewers = reviewers.suggestedReviewers ?? []

  if (completedReviews.length === 0 && pendingRequests.length === 0 && suggestedReviewers.length === 0) {
    return <span className={styles.emptyState}>No reviewers</span>
  }

  return (
    <ul className={styles.reviewerList}>
      {completedReviews.map(review => (
        <CompletedReviewRow key={review.reviewer.id} review={review} />
      ))}
      {pendingRequests.map(request => (
        <PendingReviewRow key={request.reviewer.id} request={request} />
      ))}
    </ul>
  )
}

function CompletedReviewRow({review}: {review: CompletedReview}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsOverviewRoute)
  const name = review.reviewer.displayLogin
  const {icon: stateIcon, className: stateClass} = reviewState(review.state)
  const changesWithRangePath = pullRequestsChangesWithRangeRoute.generatePath({
    owner,
    repo,
    pr_number,
    range: `BASE..${review.headSha}`,
  })

  return (
    <li className={styles.reviewerRow}>
      <ReviewerIdentity reviewer={review.reviewer} />
      <span className={styles.indicators}>
        {review.canReRequest && (
          <IconButton
            icon={SyncIcon}
            aria-label="Re-request review"
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        <IconButton
          as={Link}
          to={changesWithRangePath}
          icon={stateIcon}
          aria-label={reviewStateTooltip(review.state, name)}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, stateClass)}
        />
      </span>
    </li>
  )
}

function PendingReviewRow({request}: {request: PendingReviewRequest}) {
  const name = request.reviewer.displayLogin
  return (
    <li className={styles.reviewerRow}>
      <ReviewerIdentity reviewer={request.reviewer} />
      <span className={styles.indicators}>
        {request.deferredType !== null && (
          <IconButton
            icon={MoonIcon}
            aria-label={deferredTooltip(request.deferredType, name)}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        {(request.isCodeOwner || request.isCodeOwnerFromDelegation) && (
          <IconButton
            icon={ShieldIcon}
            aria-label={codeOwnerTooltip(request)}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        <IconButton
          icon={DotFillIcon}
          aria-label={`Awaiting requested review from ${name}`}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, 'color-fg-attention')}
        />
      </span>
    </li>
  )
}
